/**
 * VRG-675 — where an account CAME FROM: the Traction channel that produced a person or a
 * company. One value per channel in `vera-inc/gtm/channels/README.md` (the canon), keyed by
 * the same ids the Channels overview uses (`GtmChannelsPage/gtmChannels.ts`), so a row on
 * the overview and a value here are one name.
 *
 * #13 is `outreach`, not `sales`. Spencer (2026-10-07): channel #13 Sales is operated as
 * OUTREACH (email outreach and in-person city trips), and the pipeline page is "Sales". So the
 * value a person carries when an outreach email or a field visit produced them is `outreach`.
 *
 * NO separate `field_visit` value, deliberately. A field visit is a TYPE of outreach campaign
 * (in person vs email), and that fact lives on the campaign record (VRG-679). A sub-value here
 * would encode it twice and split one channel's count across two values, so "how many people
 * did Outreach bring" would need a sum nobody remembers to do.
 *
 * Absent (null) means "not recorded", NOT "direct": nobody has said where this account came
 * from. That is the honest default for every row that predates this field.
 *
 * Stored as text with a CHECK constraint (migration 0133), not a Postgres enum: adding a
 * channel is then one constraint swap, not an `ALTER TYPE … ADD VALUE` that cannot run inside
 * a transaction. Keep this list and that constraint in step — the API guard
 * `source-channel.guard.spec.ts` compares them.
 */
export const SOURCE_CHANNELS = [
  'targeting_blogs',
  'publicity',
  'unconventional_pr',
  'sem',
  'social_display_ads',
  'offline_ads',
  'seo',
  'content_marketing',
  'email_marketing',
  'viral_marketing',
  'engineering_as_marketing',
  'business_development',
  'outreach',
  'affiliate_programs',
  'existing_platforms',
  'trade_shows',
  'offline_events',
  'speaking',
  'community_building',
] as const;

export type SourceChannel = (typeof SOURCE_CHANNELS)[number];

/**
 * Traction number and display label, in the canon's order. THE ONE LABEL MAP (VRG-696): the
 * Channels overview's rows, the source-channel pickers, the Sales by-source table and the master
 * pipeline's channel nodes all read a channel's name from here, and
 * `apps/platform/test/gtm-channel-coverage.guard.spec.ts` fails if a Channels row types its own.
 *
 * Names are `vera-inc/gtm/channels/README.md`'s, with Spencer's newer decisions where vera-2 is
 * ahead of it:
 *   #5  "Social (organic)" — Spencer, 2026-10-13 (88a8e29e7): #5 is run as organic social at
 *       Focus; paid display stays out. The README row still reads "Social & display ads" /
 *       Not now. ⚠️ ONE stored value, `social_display_ads`, therefore covers organic social
 *       (operated) AND Traction's paid social/display ads (not pursued). Whether those should
 *       be two values is Spencer's call, not this file's (reported in VRG-696).
 *   #13 "Sales" — the README's name. Was "Outreach" here: the VALUE stays `outreach` (VRG-675:
 *       outreach is how Sales is operated), but the channel is Sales, the overview row says
 *       Sales and its lead-source group is Sales, so one channel read as two names.
 */
export const SOURCE_CHANNEL_META: Readonly<Record<SourceChannel, { number: number; label: string }>> = {
  targeting_blogs: { number: 1, label: 'Targeting blogs' },
  publicity: { number: 2, label: 'Publicity (PR)' },
  unconventional_pr: { number: 3, label: 'Unconventional PR' },
  sem: { number: 4, label: 'Search engine marketing' },
  social_display_ads: { number: 5, label: 'Social (organic)' },
  offline_ads: { number: 6, label: 'Offline ads' },
  seo: { number: 7, label: 'SEO' },
  content_marketing: { number: 8, label: 'Content marketing' },
  email_marketing: { number: 9, label: 'Email marketing' },
  viral_marketing: { number: 10, label: 'Viral marketing' },
  engineering_as_marketing: { number: 11, label: 'Engineering as marketing' },
  business_development: { number: 12, label: 'Partnerships' },
  outreach: { number: 13, label: 'Sales' },
  affiliate_programs: { number: 14, label: 'Affiliate programs' },
  existing_platforms: { number: 15, label: 'Existing platforms' },
  trade_shows: { number: 16, label: 'Trade shows' },
  offline_events: { number: 17, label: 'Offline events' },
  speaking: { number: 18, label: 'Speaking engagements' },
  community_building: { number: 19, label: 'Community building' },
};

export const isSourceChannel = (v: unknown): v is SourceChannel =>
  typeof v === 'string' && (SOURCE_CHANNELS as readonly string[]).includes(v);
